import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import usePermissions from '../../../hooks/usePermissions';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TapeModal from './TapeModal';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';

function TapeIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Master');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTape, setSelectedTape] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    fetchTableData().finally(() => setLoading(false));
  }, [companyId, token]);

  const fetchTableData = async () => {
    try {
      const response = await fetchData('TapeApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Tape data:', error);
      setData([]);
    }
  };

  const handleDelete = () => {
    deleteData('TapeApi', `${deleteItemId}`, token)
      .then(() => { fetchTableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
      .catch((error) => console.error('Error deleting record:', error));
  };

  const handleActivate = (itemId) => {
    activateData('TapeApi', `${itemId}`, token)
      .then(() => fetchTableData())
      .catch((error) => console.error('Error activating record:', error));
  };

  const openConfirmDialog = (itemId) => { setDeleteItemId(itemId); setConfirmDialogOpen(true); };
  const closeConfirmDialog = () => { setDeleteItemId(null); setConfirmDialogOpen(false); };

  const handleOpenModal = (mode, tape = null) => { setModalMode(mode); setSelectedTape(tape); setModalOpen(true); };
  const handleCloseModal = () => { setModalOpen(false); setSelectedTape(null); window.location.reload(); };
  const handleSaveTape = () => fetchTableData();

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const filteredData = data.filter(item => item.Name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button size="small" onClick={() => handleOpenModal('view', item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(102,126,234,0.3)' }}>
          👁️
        </Button>
      </Tooltip>
      {canEdit && (
      <Tooltip title="Edit Tape" arrow>
        <Button size="small" onClick={() => handleOpenModal('edit', item)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(17,153,142,0.3)' }}>
          ✏️
        </Button>
      </Tooltip>
      )}
      {canDelete && (
      <Tooltip title={item.IsActive ? 'Delete Tape' : 'Activate Tape'} arrow>
        <Button size="small" onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
          style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: item.IsActive ? '0 1px 4px rgba(255,107,107,0.3)' : '0 1px 4px rgba(78,205,196,0.3)' }}>
          {item.IsActive ? '🗑️' : '✅'}
        </Button>
      </Tooltip>
      )}
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Tapes..." />}

        <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Romans' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>🎞️ Tape</span>
            </h4>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
            <TextField
              fullWidth size="small" placeholder="Search by tape name..." value={searchTerm} onChange={handleSearchChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            {canCreate && (
            <Tooltip title="Create a new Tape" arrow>
              <Button variant="contained" onClick={() => handleOpenModal('create')}
                style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(102,126,234,0.4)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px' }}>+</span> Add Tape
              </Button>
            </Tooltip>
            )}
          </div>

          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 400 }} aria-label="Tape table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px' }}>Tape Name</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'right', height: '27px' }}>Rate (₹)</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', textAlign: 'right', height: '27px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.Name}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right', fontWeight: '600' }}>₹{parseFloat(item.Rate || 0).toFixed(2)}</TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>{renderActions(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage} onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to delete this Tape record?</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={closeConfirmDialog} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleDelete} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        <TapeModal open={modalOpen} onClose={handleCloseModal} onSave={handleSaveTape} TapeData={selectedTape} mode={modalMode} existingTapes={data} />
      </div>
    </Fade>
  );
}

export default TapeIndex;
